import { enumValue, type useKsI18n } from '@siclop/kstone-vue'
import { LeaveAccrualReason } from '../enums/LeaveAccrualReason'
import { LeaveCategory } from '../enums/LeaveCategory'
import type { LeaveBalance } from '../models/LeaveBalance'

/**
 * Affichage des soldes de congé (période, échéance, « expire bientôt », motifs d'acquisition), sans état : partagé
 * par la vue RH des soldes, l'onglet Congés de la fiche salarié et la carte « Mes congés » du tableau de bord.
 */

type Translate = ReturnType<typeof useKsI18n>['t']

/** Seuil d'alerte « expire bientôt » : échéance à moins de 60 jours. */
export const EXPIRING_SOON_DAYS = 60

const MS_PAR_JOUR = 24 * 60 * 60 * 1000

/** `Date` → `YYYY-MM-DD` (date locale, pour les paramètres d'API et les comparaisons). */
export function toIsoDate(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${m}-${d}`
}

/** `'2027-05-31T00:00:00'` → `'2027-05-31'` ; '' si absent. */
function jour(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : ''
}

/**
 * Période d'un solde : « 2026-2027 » quand la période chevauche deux années (congés payés, juin → mai), « 2026 »
 * pour une année civile (RTT…).
 */
export function leaveBalancePeriodLabel(
  balance: Pick<LeaveBalance, 'periodStartYear' | 'periodStart' | 'periodEnd'> & { leaveTypeCategory?: unknown }
): string {
  const debut = Number(jour(balance.periodStart).slice(0, 4)) || balance.periodStartYear
  const fin = Number(jour(balance.periodEnd).slice(0, 4))
    || (enumValue(LeaveCategory, balance.leaveTypeCategory) === LeaveCategory.PaidLeave ? debut + 1 : debut)
  return fin > debut ? `${debut}-${fin}` : String(debut)
}

/** Dernier jour de prise : l'échéance reportée par la RH si elle est plus tardive, sinon l'échéance calculée. */
export function leaveBalanceDeadline(balance: Pick<LeaveBalance, 'takingDeadline' | 'extendedDeadline'>): string {
  const calculee = jour(balance.takingDeadline)
  const reportee = jour(balance.extendedDeadline)
  return reportee && reportee > calculee ? reportee : calculee
}

/** Nombre de jours entre aujourd'hui et la date `YYYY-MM-DD` (négatif si elle est passée). */
export function daysUntil(isoDate: string, today: Date = new Date()): number {
  const [y, m, d] = isoDate.split('-').map(Number)
  const cible = Date.UTC(y, (m || 1) - 1, d || 1)
  const origine = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((cible - origine) / MS_PAR_JOUR)
}

/**
 * Vrai si des jours restent à prendre et que l'échéance tombe dans les `withinDays` jours (aujourd'hui compris) :
 * ces jours seront perdus s'ils ne sont pas posés.
 */
export function isLeaveBalanceExpiringSoon(
  balance: Pick<LeaveBalance, 'takingDeadline' | 'extendedDeadline' | 'remaining'>,
  today: Date = new Date(),
  withinDays: number = EXPIRING_SOON_DAYS
): boolean {
  const echeance = leaveBalanceDeadline(balance)
  if (!echeance || (balance.remaining ?? 0) <= 0) return false
  const reste = daysUntil(echeance, today)
  return reste >= 0 && reste < withinDays
}

/** Nombre de jours en français (« 2,5 »). */
export function formatLeaveDays(value: number | null | undefined): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(value ?? 0)
}

/** Libellé traduit du motif d'une ligne d'acquisition (nombre ou nom renvoyé par l'API). */
export function leaveAccrualReasonLabel(t: Translate, reason: unknown): string {
  switch (enumValue(LeaveAccrualReason, reason)) {
    case LeaveAccrualReason.Presence: return t('ksproject.hr.leaveAccrual.reason.presence', 'Présence')
    case LeaveAccrualReason.Entry: return t('ksproject.hr.leaveAccrual.reason.entry', 'Entrée en cours de mois')
    case LeaveAccrualReason.Exit: return t('ksproject.hr.leaveAccrual.reason.exit', 'Sortie en cours de mois')
    case LeaveAccrualReason.SickLeave: return t('ksproject.hr.leaveAccrual.reason.sickLeave', 'Arrêt maladie')
    case LeaveAccrualReason.WorkAccident:
      return t('ksproject.hr.leaveAccrual.reason.workAccident', 'Accident du travail / maladie pro')
    case LeaveAccrualReason.Unpaid: return t('ksproject.hr.leaveAccrual.reason.unpaid', 'Absence non rémunérée')
    case LeaveAccrualReason.Seniority: return t('ksproject.hr.leaveAccrual.reason.seniority', 'Ancienneté')
    case LeaveAccrualReason.Splitting: return t('ksproject.hr.leaveAccrual.reason.splitting', 'Fractionnement')
    case LeaveAccrualReason.Rounding: return t('ksproject.hr.leaveAccrual.reason.rounding', 'Arrondi')
    case LeaveAccrualReason.Ceiling: return t('ksproject.hr.leaveAccrual.reason.ceiling', 'Plafond annuel atteint')
    default: return '-'
  }
}
